import ThemedSelect from "../shared/ThemedSelect.jsx";
import { Fragment, useEffect, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  getSlaReasons,
  getSlaReport,
  listEntityRows,
  setSlaReason,
} from "../../services/apiClient.js";
import { formatDate } from "../../utils/date.js";
import Hint from "../shared/Hint.jsx";
import ReasonPickerModal from "../shared/ReasonPickerModal.jsx";
import { ReportMasthead } from "./ReportDocument.jsx";
import { useMasterCompanyNames } from "../../services/useMasterCompanies.js";

// The SLA report — legacy SLAReport.aspx layout, tidy: per milestone a DATE
// column and a Days(allowed) column. Cell colours are the legacy language the
// office knows:
//   plain  = in target          orange = over target, no reason chosen
//   green  = over target, excused        red = over target, claims' fault
// Clicking an over-target Days cell opens the reason picker (rules + reason
// catalogue live server-side; docs/business-rules/sla.md).

const STATE_CLASS = {
  in_target: "",
  oot_no_reason: "text-warning fw-semibold",
  oot_excused: "text-success fw-semibold",
  oot_claims_fault: "text-danger fw-semibold",
};

// milestone key → the day-gap column that ENDS at that milestone.
const DAYS_AFTER = {
  first_contact: "log_contact",
  scheduled_on: "contact_scheduled",
  inspected_on: "scheduled_inspected",
  typed_on: "inspected_typed",
  checked_on: "typed_checked",
  quoted_on: "checked_sent",
};

function SlaReport() {
  // Master companies are data now (Owner Data); same name keeps the diff and
  // the saved-search values identical to the old config constant.
  const COMPANY_GROUP_KEYS = useMasterCompanyNames();
  const [report, setReport] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [assessorCos, setAssessorCos] = useState([]);
  const [assessors, setAssessors] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [statuses] = useState([
    { code: "R", name: "Requested" }, { code: "I", name: "Inspected" }, { code: "Q", name: "Quoted" },
    { code: "A", name: "Accepted" }, { code: "D", name: "Declined" }, { code: "C", name: "Completed" },
  ]);
  const [f, setF] = useState({
    from: "", to: "", job_type: "standard-rq", status: "", insurer: "",
    assessor_company: "", assessor: "", only_missing: false, only_no_reason: false, only_oot: false,
  });
  const [picker, setPicker] = useState(null); // { row, columnKey, current }
  const [error, setError] = useState("");
  const navigate = useNavigate();

  async function load(params = f) {
    setError("");
    try {
      const r = await getSlaReport({
        from: params.from, to: params.to, job_type: params.job_type,
        status: params.status, insurer: params.insurer,
        assessor_company: params.assessor_company, assessor: params.assessor,
        only_missing: params.only_missing ? "1" : "", only_no_reason: params.only_no_reason ? "1" : "",
        only_oot: params.only_oot ? "1" : "",
      });
      setReport(r);
      setF((cur) => ({ ...cur, from: r.from, to: r.to }));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load the SLA report.");
    }
  }

  useEffect(() => {
    getSlaReasons().then(setReasons).catch(() => {});
    listEntityRows("insurer-companies").then(setInsurers).catch(() => {});
    listEntityRows("assessor-companies").then(setAssessorCos).catch(() => {});
    listEntityRows("assessors").then(setAssessors).catch(() => {});
    listEntityRows("job-types").then(setJobTypes).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!report) return <p className="text-muted">Loading SLA report…</p>;

  const set = (name, value) => setF((cur) => ({ ...cur, [name]: value }));

  async function saveReason(reasonId) {
    try {
      await setSlaReason(picker.row.id, picker.columnKey, reasonId);
      setPicker(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save the reason.");
      setPicker(null);
    }
  }

  // `readOnly` = the TOTAL column (KS 2026-09-16): it self-calculates from the
  // steps before it and takes no reason of its own, so no picker — the colour
  // still says in/over target, and a reason 1.0 recorded on it still reads.
  function DayCell({ row, columnKey, readOnly = false }) {
    const cell = row.cells[columnKey];
    if (cell == null) return <td className="sla-days text-muted">NA</td>;
    const clickable = !readOnly && cell.state !== "in_target";
    const hint = cell.reason
      ? `${cell.reason}${cell.claims_fault ? " (claims' fault)" : " (excused)"}${readOnly ? "" : " — click to change"}`
      : clickable
        ? "Over target — click to choose an Out-Of-Target reason"
        : readOnly && cell.state !== "in_target"
          ? "Over target — self-calculated from the steps before it; the reason goes on the step that ran over"
          : null;
    const td = (
      <td
        className={`sla-days ${clickable ? "clickable" : ""}`}
        onClick={(e) => {
          if (!clickable) return;
          e.stopPropagation();
          setPicker({ row, columnKey, current: cell.reason || "" });
        }}
      >
        <span className={STATE_CLASS[cell.state]}>{cell.days}</span>
      </td>
    );
    // Styled tooltip (Hint), not the native title — matches the rest of the app.
    return hint ? <Hint label={hint}>{td}</Hint> : td;
  }

  const nameOf = (list, id) =>
    (id && (list || []).find((r) => String(r.id) === String(id))?.name) || null;
  // What the printed masthead says this report covers (File Life's convention).
  const scopeLabel = f.insurer && String(f.insurer).startsWith("group:")
    ? `${String(f.insurer).slice(6)} (combined)`
    : nameOf(insurers, f.insurer) || nameOf(assessorCos, f.assessor_company) || "All companies";

  const selectStyle = { width: 190 };
  return (
    <div className="placeholder-panel">
      {/* Filter block (legacy layout, one tidy row set) */}
      <div className="report-controls d-flex flex-wrap align-items-end column-gap-2 row-gap-2 pb-2 border-bottom mb-2 no-print">
        <div>
          <Form.Label className="mb-0 small text-muted">From</Form.Label>
          <Form.Control size="sm" type="date" value={f.from} onChange={(e) => set("from", e.target.value)} style={{ width: 150 }} />
        </div>
        <div>
          <Form.Label className="mb-0 small text-muted">To</Form.Label>
          <Form.Control size="sm" type="date" value={f.to} onChange={(e) => set("to", e.target.value)} style={{ width: 150 }} />
        </div>
        <div>
          <Form.Label className="mb-0 small text-muted">Status</Form.Label>
          <ThemedSelect size="sm" value={f.status} onChange={(e) => set("status", e.target.value)} style={{ width: 140 }}>
            <option value="">All</option>
            {statuses.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </ThemedSelect>
        </div>
        <div>
          <Form.Label className="mb-0 small text-muted">Insurance Co</Form.Label>
          <ThemedSelect size="sm" value={f.insurer} onChange={(e) => set("insurer", e.target.value)} style={selectStyle}>
            <option value="">All</option>
            {/* Master groups (Owner Data → Master Companies — legacy CombinedCompanies) */}
            {COMPANY_GROUP_KEYS.map((g) => (
              <option key={g} value={`group:${g}`}>{g} (combined)</option>
            ))}
            <option disabled>──────────</option>
            {insurers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </ThemedSelect>
        </div>
        <div>
          <Form.Label className="mb-0 small text-muted">Assessor Co</Form.Label>
          <ThemedSelect size="sm" value={f.assessor_company} onChange={(e) => set("assessor_company", e.target.value)} style={selectStyle}>
            <option value="">All</option>
            {assessorCos.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </ThemedSelect>
        </div>
        <div>
          <Form.Label className="mb-0 small text-muted">Assessor</Form.Label>
          <ThemedSelect size="sm" value={f.assessor} onChange={(e) => set("assessor", e.target.value)} style={selectStyle}>
            <option value="">All</option>
            {assessors.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </ThemedSelect>
        </div>
        {/* Was three radios (Karen Sweatman 2026-08-04: same control as the
            claim search page). The three legacy VALUES are kept exactly as they
            were — "all" is 1.0's grouping, Standard R&Q + Mode Assist ONLY, not
            every job type (SLAHelper.GetJobTypeSQL) — and the real job types are
            listed under them, which the server already accepted by id. Standard
            R and Q stays the default, as in 1.0. */}
        <div>
          <Form.Label className="mb-0 small text-muted">Job Type</Form.Label>
          <ThemedSelect
            size="sm" value={f.job_type} onChange={(e) => set("job_type", e.target.value)} style={selectStyle}
          >
            <option value="standard-rq">Standard R and Q</option>
            <option value="mode-assist">Mode Assist</option>
            <option value="all">All (Standard R&amp;Q + Mode Assist)</option>
            {jobTypes.length ? (
              <optgroup label="Other job types">
                {jobTypes
                  .filter((t) => !["standard r&q", "mode assist"].includes(t.name.trim().toLowerCase()))
                  .map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
              </optgroup>
            ) : null}
          </ThemedSelect>
        </div>
        <Button size="sm" onClick={() => load()}>Search</Button>
      </div>

      <div className="d-flex flex-wrap column-gap-4 row-gap-1 pb-2 mb-2 border-bottom small no-print">
        <Form.Check
          type="checkbox" id="sla-only-missing" label="Only jobs that don't have entries"
          checked={f.only_missing} onChange={(e) => set("only_missing", e.target.checked)}
        />
        <Form.Check
          type="checkbox" id="sla-only-noreason" label="Only jobs without an Out-Of-Target reason"
          checked={f.only_no_reason} onChange={(e) => set("only_no_reason", e.target.checked)}
        />
        <Form.Check
          type="checkbox" id="sla-only-oot" label="Only jobs that are OOT (red/orange)"
          checked={f.only_oot} onChange={(e) => set("only_oot", e.target.checked)}
        />
      </div>

      {/* The document itself, File Life's shape (KS 2026-08-19: "print nicely
          like the file life report"): masthead + report-body + report-scroll,
          which print.css/report-document.css turn into a clean A4-landscape
          document — the hand-rolled overflow div this replaces was invisible
          to the .report-scroll/.table-responsive print rescue, so the table
          printed CLIPPED at the page edge in both browsers. */}
      <div className="report-doc report-doc-wide border rounded">
        <ReportMasthead
          report="SLA"
          title={scopeLabel}
          subtitle={`Grayburn Property Services · logged ${formatDate(report.from)} to ${formatDate(report.to)}`}
        />
        <div className="report-body">
        <div className="pb-2 small text-muted">
          {report.rows.length} job{report.rows.length === 1 ? "" : "s"} · logged {report.from} → {report.to}
        </div>
        <div className="report-scroll">
        <Table striped bordered hover size="sm" className="mb-0 align-middle report-table" style={{ fontSize: "0.82rem" }}>
          <thead className="table-surface">
            <tr>
              <th>GPS #</th>
              <th>Claim #</th>
              {report.milestones.map((m) => {
                const daysKey = DAYS_AFTER[m.key];
                const col = daysKey ? report.columns.find((c) => c.key === daysKey) : null;
                return (
                  <Fragment key={m.key}>
                    {/* No nowrap: the long milestone labels may wrap to two
                        lines so each column settles at its DATA width (a date),
                        not its label width — the dates are the floor, so a
                        label wraps rather than the table growing (Robert
                        2026-08-19, print fit). */}
                    <th>{m.label}</th>
                    {col ? <th className="sla-days">Days ({col.days})</th> : null}
                  </Fragment>
                );
              })}
              {/* 1.0's heading, back by name (KS 2026-09-16). The File Life
                  column that used to follow it is gone from this report — File
                  Life has its own report (FL.7). */}
              <Hint label="Job Inspected → Quote Sent. Self-calculated from the steps before it; a reason goes on the step that ran over.">
                <th className="sla-days">
                  TOTAL Job Quoted Turnaround ({report.columns.find((c) => c.key === "inspected_sent").days})
                </th>
              </Hint>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((row) => (
              <tr key={row.id}>
                <td className="text-nowrap">
                  <span role="button" className="text-primary clickable" onClick={() => navigate(`/claims/${row.claim_number}`)}>
                    {row.claim_number}
                  </span>
                </td>
                <td className="text-nowrap">{row.insurance_claim_number || "-"}</td>
                {report.milestones.map((m) => {
                  const date = row.dates[m.key];
                  const daysKey = DAYS_AFTER[m.key];
                  return (
                    <Fragment key={m.key}>
                      <td className="text-nowrap">
                        {date ? formatDate(date) : <span className="text-danger">Not Entered</span>}
                      </td>
                      {daysKey ? <DayCell row={row} columnKey={daysKey} /> : null}
                    </Fragment>
                  );
                })}
                <DayCell row={row} columnKey="inspected_sent" readOnly />
              </tr>
            ))}
            {report.rows.length === 0 ? (
              <tr>
                <td colSpan={2 + report.milestones.length * 2} className="text-center text-muted">
                  No jobs logged in this range.
                </td>
              </tr>
            ) : null}
          </tbody>
          <tfoot>
            <tr className="table-surface fw-semibold">
              <td colSpan={3} className="text-end text-nowrap small">Within target (incl. excused):</td>
              {report.milestones.map((m) => {
                const daysKey = DAYS_AFTER[m.key];
                if (!daysKey) return null;
                const s = report.summary.find((x) => x.key === daysKey);
                const col = report.columns.find((x) => x.key === daysKey);
                return (
                  <Fragment key={m.key}>
                    <td />
                    <td className="sla-days">
                      <span className={s.pct == null ? "text-muted" : s.pct >= col.target ? "text-success" : "text-danger"}>
                        {s.pct == null ? "—" : `${s.pct}%`}
                      </span>
                    </td>
                  </Fragment>
                );
              })}
              {["inspected_sent"].map((key) => {
                const s = report.summary.find((x) => x.key === key);
                const col = report.columns.find((x) => x.key === key);
                return (
                  <td key={key} className="sla-days">
                    <span className={s.pct == null ? "text-muted" : s.pct >= col.target ? "text-success" : "text-danger"}>
                      {s.pct == null ? "—" : `${s.pct}%`}
                    </span>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </Table>
        </div>
        </div>
      </div>

      {/* Out-Of-Target reason picker — shared with the Progress screen */}
      <ReasonPickerModal
        show={!!picker}
        title={
          picker
            ? `Out-Of-Target reason — job ${picker.row.claim_number}, ${report.columns.find((c) => c.key === picker.columnKey)?.label || ""}`
            : ""
        }
        reasons={reasons}
        columnKey={picker?.columnKey || null}
        current={picker?.current || ""}
        onSelect={(reasonId) => saveReason(reasonId)}
        onClear={() => saveReason(null)}
        onHide={() => setPicker(null)}
      />
    </div>
  );
}

export default SlaReport;
